import { Navigate, Route, Routes } from "react-router-dom";
import { AppShell } from "./components/AppShell";
import { useSession } from "./lib/session";
import { AgentKeysRoute } from "./routes/AgentKeysRoute";
import { AboutSettings, AccountSettings, SettingsRoute } from "./routes/SettingsRoute";
import { AssetRoute } from "./routes/AssetRoute";
import { AssetsRoute } from "./routes/AssetsRoute";
import { ConnectorsRoute } from "./routes/ConnectorsRoute";
import { MembersRoute } from "./routes/MembersRoute";
import { ProjectRoute } from "./routes/ProjectRoute";
import { SessionsRoute } from "./routes/SessionsRoute";
import { SharedRoute } from "./routes/SharedRoute";
import { SignInRoute } from "./routes/SignInRoute";
import { SpaceKeysRoute } from "./routes/SpaceKeysRoute";
import { SpaceLayout } from "./routes/SpaceLayout";
import { SpaceMcpRoute } from "./routes/SpaceMcpRoute";
import { SpaceRoute } from "./routes/SpaceRoute";
import { SpaceSettingsRoute } from "./routes/SpaceSettingsRoute";
import { SpacesRoute } from "./routes/SpacesRoute";

export default function App() {
  const { session } = useSession();

  if (session.state === "loading") {
    return (
      <div className="boot" role="status" aria-live="polite">
        Loading…
      </div>
    );
  }

  // A share renders before the session is consulted at all (ADR 000057). Its
  // audience has no account, so sending them to a sign-in page, or making them
  // wait on a /me round trip, would defeat the whole thing.
  //
  // Checked on the path rather than routed inside the shell, because the shell
  // is where the session lives and a share has none.
  if (window.location.pathname.startsWith("/shared/")) {
    return (
      <Routes>
        <Route path="/shared/:token" element={<SharedRoute />} />
      </Routes>
    );
  }

  if (session.state === "signed-out") {
    return <SignInRoute ended={session.ended} />;
  }

  return (
    <AppShell user={session.user}>
      <Routes>
        <Route path="/" element={<Navigate to="/spaces" replace />} />
        <Route path="/spaces" element={<SpacesRoute />} />
        {/* Keys live under settings, like every other account-level thing. The
            old path still works, because a key config someone pasted into a
            client should not stop resolving. */}
        <Route path="/settings" element={<SettingsRoute />}>
          <Route index element={<AccountSettings />} />
          <Route path="keys" element={<AgentKeysRoute />} />
          <Route path="connectors" element={<ConnectorsRoute />} />
          <Route path="sessions" element={<SessionsRoute />} />
          <Route path="about" element={<AboutSettings />} />
        </Route>
        <Route path="/agent-keys" element={<Navigate to="/settings/keys" replace />} />
        {/* Every screen inside a space shares SpaceLayout's nav rail, so
            reaching one destination from another never detours through the
            landing page (issue #41). A key belongs to one space, so it is
            made from inside that space rather than from account settings
            (ADR 000041).

            Connecting lives at /connect, not /mcp: the protocol owns
            /spaces/{id}/mcp and answers POST there (ADR 000043). */}
        <Route path="/spaces/:spaceId" element={<SpaceLayout />}>
          <Route index element={<SpaceRoute />} />
          <Route path="members" element={<MembersRoute />} />
          <Route path="keys" element={<SpaceKeysRoute />} />
          <Route path="connect" element={<SpaceMcpRoute />} />
          <Route path="settings" element={<SpaceSettingsRoute />} />
          <Route path="content" element={<AssetsRoute />} />
          <Route path="content/:assetId" element={<AssetRoute />} />
          <Route path="projects/:projectId" element={<ProjectRoute />} />
        </Route>
        <Route path="*" element={<Navigate to="/spaces" replace />} />
      </Routes>
    </AppShell>
  );
}
